// Everything a case screen needs, computed once per active document and shared through context.
import { createContext, useContext, useMemo } from 'react';
import { normalizeEngineCase, type NCase } from '@divorceos/engine';
import type { Case, CaseResult } from '@divorceos/schema';
import { appliedFc4055, changedSettings, partyLabels, resolveFc4055, type ChangedSetting, type Fc4055Resolved } from './model';
import type { Doc } from './store';
import { useEngine } from '../store/useResult';
import { useDebounced } from './useDebounced';

export interface CaseView {
  doc: Doc;
  /** Current inputs. */
  c: Case;
  /** Inputs of the last calculation (equals `c` while auto-recalculate is on). */
  calc: Case;
  result: CaseResult | null;
  engineError: string | null;
  labels: [string, string];
  /** Engine view of the inputs (resolved exemptions, ages); null if the case cannot be normalised. */
  nc: NCase | null;
  /** FC 4055 version the setting resolves to, and what the engine applies. */
  fcResolved: Fc4055Resolved;
  fcApplied: Fc4055Resolved;
  /** What Automatic would give (for the notice). */
  fcAuto: Fc4055Resolved;
  changed: ChangedSetting[];
  stale: boolean;
}

export const CaseViewContext = createContext<CaseView | null>(null);

export function useCaseView(): CaseView {
  const v = useContext(CaseViewContext);
  if (!v) throw new Error('useCaseView outside a case');
  return v;
}

export function useBuildCaseView(doc: Doc): CaseView {
  const c = doc.data;
  const labels = useMemo(() => partyLabels(c, doc.prefs.labels), [c, doc.prefs.labels]);
  const calcLabels = useMemo(() => partyLabels(doc.calcData, doc.prefs.labels), [doc.calcData, doc.prefs.labels]);
  const calcData = useDebounced(doc.calcData, 120);
  const eng = useEngine(calcData, calcLabels);
  const nc = useMemo(() => { try { return normalizeEngineCase(c); } catch { return null; } }, [c]);
  return useMemo<CaseView>(() => {
    const autoCase = { taxYear: c.taxYear, settings: { ...c.settings, fc4055Version: 'AUTO' as const } };
    return {
      doc, c, calc: doc.calcData, result: eng.result, engineError: eng.engineError, labels, nc,
      fcResolved: resolveFc4055(c), fcApplied: appliedFc4055(doc.calcData), fcAuto: resolveFc4055(autoCase),
      changed: changedSettings(c), stale: doc.calcData !== c,
    };
  }, [doc, c, eng, labels, nc]);
}
